import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Headphones, CheckCircle, ArrowRight, ShoppingCart, Sparkles, Info, Video } from 'lucide-react';

type AudioItem = {
  title: string;
  description: string;
  isAphantasiaFriendly?: boolean;
  purchaseHref?: string;
};

const audioItems: AudioItem[] = [
  {
    title: 'Inner Calm and Strength – Hypnosis',
    description: 'Build inner steadiness and resilience so you can respond to life with clarity and confidence.',
    purchaseHref: 'https://sharonclare-hypnotherapy.kit.com/products/inner-calm-and-strength-hypnosi',
  },
  {
    title: 'Float and Unwind: Hypnosis for Sleep and Anxiety Relief',
    description: 'Soothing relaxation to quiet the mind and ease the body into restorative sleep.',
    purchaseHref: 'https://sharonclare-hypnotherapy.kit.com/products/float-and-unwind-hypnosis-for-s',
  },
  {
    title: 'Sleep Gifted: Tropical Island',
    description: 'A gentle tropical journey designed to release the day and drift into deep rest.',
    purchaseHref: 'https://sharonclare-hypnotherapy.kit.com/products/sleep-gifted-tropical-island',
  },
  {
    title: 'Reset and Restore: Hypnosis for Chronic Fatigue',
    description: 'Calm the nervous system and rebuild energy with supportive mind–body suggestions.',
    purchaseHref: 'https://sharonclare-hypnotherapy.kit.com/products/reset-restore-hypnosis-for-chro',
  },
  {
    title: 'Release the Grip: Jaw Tension & Teeth Grinding (A)',
    description: 'Ease jaw tension and bruxism with safe, grounded guidance that works without visual imagery.',
    isAphantasiaFriendly: true,
    purchaseHref: 'https://sharonclare-hypnotherapy.kit.com/products/release-the-grip-hypnosis-audio',
  },
  {
    title: 'The Pain Dial – Reclaiming Inner Control',
    description: 'Learn to turn down uncomfortable sensations and reconnect with comfort and control.',
    purchaseHref: 'https://sharonclare-hypnotherapy.kit.com/products/the-pain-dial-reclaiming-inner-',
  },
  {
    title: 'Pre‑Surgery Calm',
    description: 'Settle the mind and body before a procedure so you can feel steady and supported.',
    purchaseHref: 'https://sharonclare-hypnotherapy.kit.com/products/pre-surgery-calm-guided-self-hypnosis',
  },
  {
    title: 'Recovery After Surgery',
    description: 'Encourage rest, resilience, and gentle progress during recovery.',
    purchaseHref: 'https://www.sharonclare-hypnotherapy.com/2soothing-hypnosis-audios-for-stress-relief-anytime',
  },
];

const Audios: React.FC = () => {
  return (
    <main>
      <Helmet>
        <title>Soothing Hypnosis Audios for Stress Relief | Sharon Clare Hypnotherapy</title>
        <meta
          name="description"
          content="Professional hypnosis audios to support sleep, reduce anxiety, and unlock your inner potential – anytime, anywhere."
        />
        <link rel="canonical" href="https://www.sharonclare-hypnotherapy.com/audios/" />
      </Helmet>

      {/* Hero */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-primary-50 to-accent-50">
        <div className="container-custom">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-heading font-bold text-gray-900 mb-4">
              Soothing Hypnosis Audios for Stress Relief Anytime
            </h1>
            <p className="text-lg sm:text-xl text-gray-600 mb-2">
              Professional hypnosis audios to support <a href="/anxiety-stress" className="text-accent-600 hover:text-accent-700 underline">sleep and anxiety</a>, reduce stress, and unlock your inner potential – anytime, anywhere. Complement your <a href="/services" className="text-accent-600 hover:text-accent-700 underline">hypnotherapy sessions</a> or use standalone.
            </p>
            <p className="text-xl font-heading text-gray-900 mt-2">Feel Better, Think Brighter – One Download at a Time</p>
          </div>
        </div>
      </section>

      {/* Video intro */}
      <section className="py-12 bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
              <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100">
              <div className="flex items-center space-x-3 mb-4">
                <Video className="w-6 h-6 text-accent-600" />
                <h2 className="text-xl sm:text-2xl font-heading font-semibold text-gray-900">
                  What is Hypnosis? (Short Video)
                </h2>
              </div>
              <div className="aspect-video w-full rounded-lg overflow-hidden bg-gray-100">
                <iframe
                  width="560"
                  height="315"
                  src="https://www.youtube.com/embed/yKweu2C76Ac?si=eCXX9kofcYi4eCzD"
                  title="YouTube video player"
                  frameBorder={0}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  referrerPolicy="strict-origin-when-cross-origin"
                  allowFullScreen
                  className="w-full h-full"
                />
              </div>
            </div>
              <div className="grid md:grid-cols-2 gap-6 mt-6">
                {[
                  { title: 'Pain & the Brain (Short)', url: 'https://youtube.com/shorts/NUBeOym1p1o?feature=share' },
                  { title: 'Stop Smoking/Vaping (Short)', url: 'https://youtube.com/shorts/DFXbCsLf4GI?feature=share' },
                  { title: 'Boost Feel‑Good Chemicals (Short)', url: 'https://youtube.com/shorts/63p-CSOl1l0?feature=share' },
                  { title: 'The Stress Bucket (Short)', url: 'https://youtube.com/shorts/YOdlFrwp_zM?feature=share' },
                ].map((v) => (
                  <a key={v.url} href={v.url} target="_blank" rel="noopener noreferrer" className="block bg-white rounded-2xl p-4 shadow border border-gray-100 hover:shadow-md transition">
                    <div className="flex items-center space-x-3">
                      <Video className="w-5 h-5 text-accent-600" />
                      <div>
                        <p className="font-medium text-gray-900">{v.title}</p>
                        <p className="text-sm text-gray-600">Watch on YouTube</p>
                      </div>
                    </div>
                  </a>
                ))}
              </div>
          </div>
        </div>
      </section>

      {/* What is hypnosis */}
      <section className="py-12 bg-gradient-to-br from-accent-50 to-primary-50">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto text-gray-700">
            <h2 className="text-2xl sm:text-3xl font-heading font-bold text-gray-900 mb-4 text-center">What is Hypnosis?</h2>
            <p className="text-lg leading-relaxed mb-4">
              You don’t have to be asleep, out of control, or “under” for hypnosis to work. Hypnosis is a natural, focused state
              of awareness you likely dip into daily – daydreaming, getting lost in a book, or driving on autopilot.
            </p>
            <p className="text-lg leading-relaxed">
              In this state your subconscious mind becomes more receptive to helpful suggestions – allowing you to update patterns,
              release anxiety, improve sleep, and build emotional strength from within.
            </p>
          </div>
        </div>
      </section>

      {/* Benefits list */}
      <section className="py-12 bg-white">
        <div className="container-custom">
          <div className="max-w-5xl mx-auto grid md:grid-cols-2 gap-8">
            <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100">
              <div className="flex items-center space-x-3 mb-4">
                <Headphones className="w-6 h-6 text-accent-600" />
                <h3 className="text-xl font-heading font-semibold text-gray-900">These recordings are designed to:</h3>
              </div>
              <ul className="space-y-3">
                {[
                  'Help you feel calm, safe, and in control',
                  'Reduce mental chatter and physical tension',
                  'Rewire automatic responses like worry or overthinking',
                  'Reconnect you to your strengths, goals, and sense of possibility',
                ].map((item) => (
                  <li key={item} className="flex items-start space-x-3">
                    <CheckCircle className="w-5 h-5 text-green-500 mt-0.5" />
                    <span className="text-gray-700">{item}</span>
                  </li>
                ))}
              </ul>
              <p className="text-gray-600 mt-4 text-sm">
                Every audio blends Solution-Focused Hypnotherapy, NLP, and modern neuroscience.
              </p>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100">
              <h3 className="text-xl font-heading font-semibold text-gray-900 mb-3">Browse Hypnosis Audios</h3>
              <p className="text-gray-600 mb-4">Each audio is around 20–25 minutes and crafted to guide your mind into a relaxed state where positive change feels natural.</p>
              <p className="text-sm text-accent-700 font-medium"><CheckCircle className="inline w-4 h-4 mr-1 -mt-0.5" aria-hidden="true" />(A) symbol = designed for people who don’t visualise</p>
            </div>
          </div>
        </div>
      </section>

      {/* Audio grid */}
      <section className="py-16 bg-white">
        <div className="container-custom">
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {audioItems.map((item, idx) => (
              <div key={idx} className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100 flex flex-col">
                <div className="mb-4">
                  <h4 className="text-lg font-heading font-semibold text-gray-900">{item.title}</h4>
                  <p className="text-sm text-gray-600 mt-1">{item.description}</p>
                </div>
                <div className="mt-auto flex items-center justify-end">
                  <div className="flex items-center space-x-2">
                    {item.isAphantasiaFriendly && (
                      <span className="text-xs bg-accent-100 text-accent-700 px-2 py-1 rounded-full" title="Aphantasia-friendly">
                        (A)
                      </span>
                    )}
                    <a
                      href={item.purchaseHref || `mailto:Sharon@sharonclare-hypnotherapy.com?subject=${encodeURIComponent('Audio Purchase: ' + item.title)}&body=${encodeURIComponent('Hi Sharon, I would like to purchase "' + item.title + '". Please send the payment link. Thank you!')}`}
                      target={item.purchaseHref ? '_blank' : undefined}
                      rel={item.purchaseHref ? 'noopener noreferrer' : undefined}
                      className="inline-flex items-center px-3 py-2 rounded-lg bg-blue-600 text-white text-sm hover:bg-blue-700"
                      onClick={async () => {
                        // Track audio download
                        if (item.purchaseHref) {
                          try {
                            await fetch('/api/track-audio', {
                              method: 'POST',
                              headers: { 'Content-Type': 'application/json' },
                              body: JSON.stringify({
                                audioTitle: item.title,
                                audioUrl: item.purchaseHref,
                                source: 'audios-page'
                              })
                            });
                          } catch (err) {
                            // Silent fail - don't interrupt user flow
                            console.error('Failed to track audio download', err);
                          }
                        }
                      }}
                    >
                      <ShoppingCart className="w-4 h-4 mr-1" /> Get the Audio
                    </a>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Bespoke section */}
      <section className="py-16 bg-gradient-to-br from-accent-50 to-primary-50">
        <div className="container-custom max-w-5xl">
          <div className="grid lg:grid-cols-2 gap-8 items-start">
            <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100">
              <h3 className="text-2xl font-heading font-semibold text-gray-900 mb-3"><Sparkles className="inline w-6 h-6 mr-2 -mt-1 text-accent-600" aria-hidden="true" />Want Something Created Just for You?</h3>
              <p className="text-gray-700 mb-4">
                Request a bespoke hypnosis recording tailored to your goals — anxiety, sleep, confidence, motivation, a specific fear, or something else.
              </p>
              <ul className="space-y-2 text-gray-700 mb-4">
                <li>– 20–30 minute professionally recorded hypnosis audio (MP3)</li>
                <li>– Created around the outcome you’d like support with</li>
                <li>– Delivered within 5–7 working days</li>
              </ul>
              <p className="text-gray-700 mb-4">
                After ordering, you can complete a short form or book a quick call. We’ll clarify your goals, and I’ll create your personalised recording.
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <a href="/contact" className="btn-primary inline-flex items-center justify-center">
                  Order Bespoke Audio
                  <ArrowRight className="w-4 h-4 ml-2" />
                </a>
                <a href="mailto:Sharon@sharonclare-hypnotherapy.com" className="btn-secondary inline-flex items-center justify-center">
                  Ask a Question
                </a>
              </div>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100">
              <h3 className="text-2xl font-heading font-semibold text-gray-900 mb-3">“I Can’t Visualise – Can Hypnosis Still Work for Me?”</h3>
              <p className="text-gray-700 mb-3">Yes — absolutely. Not everyone visualises in pictures, and that’s completely okay.</p>
              <p className="text-gray-700 mb-3">
                Recordings marked with (A) are written to work beautifully without visual imagery. They use descriptive metaphors, suggestive language that activates emotion and meaning, and safe, grounded guidance that helps your inner system shift.
              </p>
              <p className="text-gray-700">You don’t need to picture anything. You just need to listen, and let your mind respond in its own way.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Why choose */}
      <section className="py-16 bg-white">
        <div className="container-custom max-w-5xl">
          <h3 className="text-2xl font-heading font-semibold text-gray-900 mb-6 text-center">Why Choose These Audios?</h3>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { title: 'Professionally recorded', text: 'Created by a qualified clinical hypnotherapist (CNHC, AfSFH).' },
              { title: 'Evidence-based', text: 'Grounded in Solution-Focused Hypnotherapy and neuroscience.' },
              { title: 'Relaxing & safe', text: 'Designed to help you feel soothed and supported.' },
              { title: 'Flexible support', text: 'Listen anytime, in your own home, at your own pace.' },
            ].map(({ title, text }) => (
              <div key={title} className="bg-white rounded-xl p-6 shadow-lg border border-gray-100">
                <h4 className="font-semibold text-gray-900 mb-2">{title}</h4>
                <p className="text-gray-600 text-sm">{text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Disclaimer */}
      <section className="py-12 bg-gradient-to-br from-primary-50 to-accent-50">
        <div className="container-custom max-w-4xl">
          <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100">
            <div className="flex items-center space-x-3 mb-3">
              <Info className="w-5 h-5 text-accent-600" />
              <h4 className="font-semibold text-gray-900">Important Disclaimer</h4>
            </div>
            <ul className="text-gray-700 space-y-2 text-sm">
              <li>– These recordings support general wellbeing and are not a substitute for medical advice or treatment.</li>
              <li>– If you’re concerned about your health, consult a qualified healthcare provider.</li>
              <li>– Never listen while driving or operating machinery. Use in a safe, comfortable place.</li>
            </ul>
          </div>
        </div>
      </section>
    </main>
  );
};

export default Audios;


